import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaPlus } from 'react-icons/fa';

import { useStock } from '../../context/StockProvider';

import OutletSearch from '../SelectSearch/OutletSearch';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { formatSalesValue } from '../../utils/formatUtils';
import ProductionInputTable from './ProductionInputTable';
import PageStatus from '../PageStatus/PageStatus';
import { selectStyles } from '../../utils/cssUtils';

const editableClass =
    "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600";

const readonlyClass =
    "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed";

const CentralKitchenProduction = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");
    const today = new Date().toLocaleDateString('en-CA');
    const [selectedOutlet, setSelectedOutlet] = useState(null);

    const {
        stockItems,
        stockLoading,
        stockError,
    } = useStock();

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        date: today,
        total: 0,
        items: [
            {
                ItemName: "",
                Quantity: "",
                UOM: "",
                Rate: "",
                Total: "",
                Unit: ""
            }
        ]
    });

    const finishedGoodsOptions = useMemo(() => {
        return stockItems
            ?.filter(
                (item) =>
                    item.GroupName?.toLowerCase() ===
                    "finished goods"
            )
            ?.map((item) => ({
                label: `${item.ItemName} (${item.UOM})`,
                value: item.ItemName,
                item
            }));
    }, [stockItems]);

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];

        updatedItems[index][field] = value;

        const qty = parseFloat(updatedItems[index].Quantity) || 0;
        const rate = parseFloat(updatedItems[index].Rate) || 0;
        const unit = parseFloat(updatedItems[index].Unit) || 1;

        console.log(qty, rate, unit)

        updatedItems[index].Total = (qty * rate) / unit;

        const grandTotal = updatedItems.reduce(
            (sum, item) =>
                sum + (parseFloat(item.Total) || 0),
            0
        );

        setFormData((prev) => ({
            ...prev,
            items: updatedItems,
            total: grandTotal
        }));
    };

    const handleSelectItem = (selected, index) => {
        if (!selected) return;

        const stockItem = selected.item;

        const updatedItems = [...formData.items];

        updatedItems[index] = {
            ...updatedItems[index],
            ItemName: stockItem.ItemName,
            UOM: stockItem.unit_definitions[0].uom || stockItem.UOM,
            Rate: stockItem.Rate,
            Total:
                (parseFloat(updatedItems[index].Quantity) || 0) *
                (parseFloat(stockItem.Rate) || 0) /
                (parseFloat(stockItem.unit_definitions[0].unit) || 1),
            Unit: stockItem.unit_definitions[0].unit
        };

        const grandTotal = updatedItems.reduce(
            (sum, item) =>
                sum + (parseFloat(item.Total) || 0),
            0
        );

        setFormData((prev) => ({
            ...prev,
            items: updatedItems,
            total: grandTotal
        }));
    };

    const addRow = () => {
        const hasEmptyQuantity = formData.items.some(
            (item) => item.Quantity === "" || item.Quantity === null || isNaN(parseFloat(item.Quantity))
        );

        if (hasEmptyQuantity) {
            toast.error("Please fill quantity for all items before adding a new row");
            return;
        }

        setFormData((prev) => ({
            ...prev,
            items: [
                ...prev.items,
                {
                    ItemName: "",
                    Quantity: "",
                    UOM: "",
                    Rate: "",
                    Total: "",
                    Unit: ""
                }
            ]
        }));
    };

    const removeRow = (index) => {
        const updatedItems = formData.items.filter(
            (_, i) => i !== index
        );

        const grandTotal = updatedItems.reduce(
            (sum, item) =>
                sum + (parseFloat(item.Total) || 0),
            0
        );

        setFormData((prev) => ({
            ...prev,
            items: updatedItems,
            total: grandTotal
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedOutlet) {
            toast.error("Please select outlet");
            return;
        }

        const hasEmptyQuantity = formData.items.some(
            (item) => item.Quantity === "" || item.Quantity === null || isNaN(parseFloat(item.Quantity))
        );

        if (hasEmptyQuantity) {
            toast.error("Please fill quantity for all items before posting");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                date: formData.date,
                outlet: selectedOutlet,
                total: formData.total,
                items: formData.items.map((item) => ({
                    ItemName: item.ItemName,
                    Quantity: Number(item.Quantity),
                    UOM: item.UOM,
                    Rate: Number(item.Rate),
                    Total: Number(item.Total)
                }))
            };

            await axios.post(
                `${baseUrl}/postcentralkitchenproduction`,
                payload
            );

            toast.success("Production posted successfully");

            setFormData({
                date: new Date().toISOString().split("T")[0],
                total: 0,
                items: [
                    {
                        ItemName: "",
                        Quantity: "",
                        UOM: "",
                        Rate: "",
                        Total: "",
                        Unit: ""
                    }
                ]
            });
        } catch (err) {
            console.error(err);

            toast.error(
                err?.response?.data?.error || err?.response?.data?.message ||
                "Failed to post production"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />

            <div className="flex-1 p-3">
                <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                    <h1 className="text-lg font-semibold text-gray-800 mb-3">
                        Central Kitchen Production History &nbsp; --- &nbsp;
                        <span className="text-sm text-gray-500">
                            Post production items to stock
                        </span>
                    </h1>

                    <form onSubmit={handleSubmit}>
                        <div className="flex gap-10 mb-4 items-center">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Outlet
                                </label>

                                <OutletSearch
                                    selectedOutlet={selectedOutlet}
                                    setSelectedOutlet={setSelectedOutlet}
                                    token={token}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Date
                                </label>

                                <input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            date: e.target.value
                                        }))
                                    }
                                    className={editableClass}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Grand Total
                                </label>

                                <input
                                    type="text"
                                    value={formatSalesValue(formData.total)}
                                    readOnly
                                    className={readonlyClass}
                                />
                            </div>
                        </div>

                        <PageStatus
                            loading={stockLoading}
                            error={stockError}
                            loadingText="Loading stock items..."
                        />

                        {!stockLoading && !stockError && (
                            <>
                                <ProductionInputTable
                                    formData={formData}
                                    finishedGoodsOptions={finishedGoodsOptions}
                                    handleSelectItem={handleSelectItem}
                                    handleItemChange={handleItemChange}
                                    removeRow={removeRow}
                                    selectStyles={selectStyles}
                                    editableClass={editableClass}
                                    readonlyClass={readonlyClass}
                                    formatSalesValue={formatSalesValue}
                                />

                                <div className="mt-3 flex flex-wrap items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={addRow}
                                        className="inline-flex items-center gap-1 text-sm px-4 py-2 border border-blue-200 text-blue-600 rounded-md hover:bg-blue-50"
                                    >
                                        <FaPlus size={12} />
                                        Add Row
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {loading
                                            ? "Posting..."
                                            : "Post Production"}
                                    </button>
                                </div>
                            </>
                        )}
                    </form>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default CentralKitchenProduction;